import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type Locale = 'en' | 'ar' | 'eg-ar';

const RTL_LOCALES = new Set<Locale>(['ar', 'eg-ar']);

export function dirFor(locale: Locale): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface UiState {
  theme: Theme;
  locale: Locale;
  sidebarCollapsed: boolean;
  commandPaletteOpen: boolean;
  setTheme: (theme: Theme) => void;
  setLocale: (locale: Locale) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      locale: 'en',
      sidebarCollapsed: false,
      commandPaletteOpen: false,
      setTheme: (theme) => set({ theme }),
      setLocale: (locale) => set({ locale }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
    }),
    {
      name: 'plustown.ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        theme: s.theme,
        locale: s.locale,
        sidebarCollapsed: s.sidebarCollapsed,
      }),
    }
  )
);

/**
 * Apply theme + dir to the document root. Called on store-change subscription
 * from app/providers.tsx and also on first paint (inline script in index.html).
 */
export function applyDocumentChrome(state: Pick<UiState, 'theme' | 'locale'>): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.dataset.theme = resolveTheme(state.theme);
  html.lang = state.locale;
  html.dir = dirFor(state.locale);
}
